import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionType } from '../../../generated/hooks';
import { AnswerGroupTable, ColumnType } from '../components/AnswerGroupTable';
import { questionSummaryKey } from '@arnold/core';
import { HeadingMedium, LinkButton, SideModal, theme } from '@arnold/common';
import { localizedText, type ReportTableColumn, type ReportTableConfiguration } from '../definition/types';
import { answeredRows } from '../definition/tableRows';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup } from '../types';
import { getPresetColumns, getPresetQuestion, isTextColumn } from './tableTestColumns';
import { Action, Bar, Page, Stack } from './tablePlaygroundStyles';
import { CheckboxRow } from '../components/FilterDrawer/CheckboxRow';
import styled from '@emotion/styled';

const EMPTY_GROUPS: AnswerGroup[] = [];

export const DeepAnalysisPage = ({
  report,
  initialConfiguration,
  onConfigurationChange,
  embedded = false,
  language,
}: {
  report: NewReport;
  initialConfiguration?: ReportTableConfiguration;
  onConfigurationChange?: (configuration: ReportTableConfiguration) => void;
  embedded?: boolean;
  language?: string;
}) => {
  const { t, i18n } = useTranslation('newReport');
  const labelLanguage = language ?? i18n.language;
  const { selectRows, questionScoring } = report;
  const metrics = useDefinitionMetrics();
  const questions = useMemo(
    () =>
      (report.rawData?.questions ?? []).filter((question) =>
        [QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale, QuestionType.Freetext].includes(
          question.type,
        ),
      ),
    [report.rawData],
  );
  const [configs, setConfigs] = useState<ReportTableColumn[]>(
    () =>
      initialConfiguration?.columns ??
      getPresetColumns('metrics', questions, getPresetQuestion('metrics', questions), metrics),
  );
  const [pageSize] = useState(initialConfiguration?.pageSize ?? 10);
  const [textFilterColumnId] = useState(initialConfiguration?.nonEmptyTextColumnId ?? '');
  const [responseQuestionIndex] = useState(initialConfiguration?.responseQuestionIndex ?? '');
  // No manual "compare groups" toggle: filters alone decide whether we show one view or two.
  const hasActiveFilters = report.filters.teamIds.size > 0 || report.filters.answerSelections.length > 0;
  const roots = useMemo(() => {
    if (!hasActiveFilters) return report.filteredData ?? EMPTY_GROUPS;
    const baseline: AnswerGroup | null = report.wholeReportGroup
      ? { ...report.wholeReportGroup, comparisonRole: 'baseline' }
      : null;
    const filtered: AnswerGroup | null = report.currentGroup
      ? { ...report.currentGroup, comparisonRole: 'filtered' }
      : null;
    return [baseline, filtered].filter((group): group is AnswerGroup => group != null);
  }, [hasActiveFilters, report.wholeReportGroup, report.currentGroup, report.filteredData]);
  const projectedRows = useMemo(() => selectRows(roots, 'teams'), [selectRows, roots]);
  const textColumns = useMemo(() => configs.filter(isTextColumn), [configs]);
  const textFilterColumn = textColumns.find((column) => column.id === textFilterColumnId);
  const rows = useMemo(() => {
    const visibleRows = responseQuestionIndex ? answeredRows(projectedRows, responseQuestionIndex) : projectedRows;
    if (!textFilterColumn) return visibleRows;
    const keepContent = (group: AnswerGroup): AnswerGroup | null => {
      const summary = group.questionSummaries.get(
        questionSummaryKey(textFilterColumn.questionIndex, textFilterColumn.statementIndex),
      );
      const entries = textFilterColumn.source === 'comment' ? summary?.commentEntries : summary?.textEntries;
      if (!entries?.length) return null;
      return {
        ...group,
        children: group.children.map(keepContent).filter((child): child is AnswerGroup => child !== null),
      };
    };
    return visibleRows.map(keepContent).filter((group): group is AnswerGroup => group !== null);
  }, [projectedRows, textFilterColumn, responseQuestionIndex]);

  const columnLabel = useCallback(
    (column: ReportTableColumn) => {
      const translation = localizedText(column.translations, labelLanguage);
      if (translation) return translation;
      if (column.label) return column.label;
      if ('metricId' in column)
        return localizedText(metrics.find((metric) => metric.id === column.metricId)?.title, labelLanguage);
      let label = t(`table.columns.${column.type}`);
      if (!('questionIndex' in column)) return label;
      const question = questions.find((item) => item.index === column.questionIndex);
      const nps = questionScoring[column.questionIndex]?.nps;
      if ([ColumnType.NPS, ColumnType.MINI_NPS].includes(column.type) && nps)
        label = t('tableTest.npsColumnLabel', { label, min: nps.minValue ?? 0 });
      if (isTextColumn(column) && column.source === 'comment') label = t('tableTest.commentColumnLabel', { label });
      return [
        label,
        question?.text ?? column.questionIndex,
        column.statementIndex === undefined ? undefined : question?.statementsLocal?.[column.statementIndex],
      ]
        .filter(Boolean)
        .join(' · ');
    },
    [questions, questionScoring, metrics, labelLanguage, t],
  );
  const columns = useMemo(
    () =>
      configs.map((column) => {
        const format =
          'metricId' in column
            ? metrics.find((metric) => metric.id === column.metricId)?.presentation.format
            : undefined;
        return { ...column, ...(format && { format }), label: columnLabel(column) };
      }),
    [configs, columnLabel, metrics],
  );
  const columnOrder = useMemo(() => columns.map((column) => column.id), [columns]);
  const changeColumnOrder = (ids: string[]) => {
    const positions = new Map(ids.map((id, index) => [id, index]));
    setConfigs((previous) =>
      [...previous].sort((a, b) => (positions.get(a.id) ?? ids.length) - (positions.get(b.id) ?? ids.length)),
    );
  };
  // The identity column is required for row expansion/labeling; it can never be hidden.
  const lockedColumnId = useMemo(() => columns.find((column) => column.type === ColumnType.LABEL)?.id, [columns]);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setHiddenColumnIds((previous) => {
      const validIds = new Set(columns.map((column) => column.id));
      const next = new Set([...previous].filter((id) => id !== lockedColumnId && validIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [columns, lockedColumnId]);
  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumnIds.has(column.id)),
    [columns, hiddenColumnIds],
  );
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const toggleColumnVisibility = (id: string) => {
    if (id === lockedColumnId) return;
    setHiddenColumnIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const showAllColumns = () => setHiddenColumnIds(new Set());
  const hideAllColumns = () =>
    setHiddenColumnIds(new Set(columns.filter((column) => column.id !== lockedColumnId).map((column) => column.id)));
  const resetKey = JSON.stringify([
    report.rawData?.id,
    hasActiveFilters,
    responseQuestionIndex,
    [...report.filters.teamIds].sort(),
    report.filters.answerSelections,
    textFilterColumn
      ? [textFilterColumn.id, textFilterColumn.questionIndex, textFilterColumn.statementIndex, textFilterColumn.source]
      : null,
  ]);

  return (
    <Page>
      <Bar>
        <h2>{t('definition.preview')}</h2>
        <Action type="button" onClick={() => setColumnsModalOpen(true)}>
          {t('table.columnVisibility.chooseColumns')}
        </Action>
      </Bar>
      <AnswerGroupTable
        groups={rows}
        columns={visibleColumns}
        pageSize={pageSize}
        horizontalScroll={true}
        columnResizeEnabled={true}
        sortingEnabled={true}
        searchEnabled={true}
        columnOrderingEnabled={true}
        columnOrder={columnOrder}
        onColumnOrderChange={changeColumnOrder}
        onColumnWidthsChange={
          onConfigurationChange
            ? (widths) =>
                setConfigs((previous) =>
                  previous.map((column) => ({ ...column, width: widths[column.id] ?? column.width })),
                )
            : undefined
        }
        resetKey={resetKey}
        label={t('tableTest.title')}
      />
      <SideModal
        opened={columnsModalOpen}
        onCloseAction={() => setColumnsModalOpen(false)}
        header={<HeadingMedium>{t('table.columnVisibility.title')}</HeadingMedium>}
        primaryButton={{ label: t('table.columnVisibility.confirm'), onClick: () => setColumnsModalOpen(false) }}
        backgroundColor={theme.background.primary}
      >
        <Stack>
          <Bar>
            <LinkButton onClick={showAllColumns}>{t('table.columnVisibility.showAll')}</LinkButton>
            <LinkButton onClick={hideAllColumns}>{t('table.columnVisibility.hideAll')}</LinkButton>
          </Bar>
          <Stack>
            {columns.map((column) => (
              <StyledCheckboxRow
                key={column.id}
                label={column.label}
                selected={!hiddenColumnIds.has(column.id)}
                disabled={column.id === lockedColumnId}
                onClick={() => toggleColumnVisibility(column.id)}
              />
            ))}
          </Stack>
        </Stack>
      </SideModal>
    </Page>
  );
};

const StyledCheckboxRow = styled(CheckboxRow)`
  gap: 0;
  padding: 0;
`;
